/**
 * SQL 변조 기준 편집 — 이름 매핑(변조 전 → 변조 후) · 바꾸지 않을 이름 · 자동 이름(종류별 켜기·접두어). docs/features/29-sql-collect.md §변조 기준.
 * 기준은 출발점이다 — 세트에서 변조할 때 기준을 고르고, 그 세트에서 고친 매핑은 「기준에 반영」 으로 다시 더한다.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiPlus, FiX } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/sqlCollect';
import WorkflowHead from '../common/WorkflowHead';

export const RULE_KINDS = ['schema', 'table', 'column', 'index', 'function', 'guess'];
const parseKeep = (text) => [...new Set(String(text || '').split(/[\s,]+/).map(x => x.trim().toUpperCase()).filter(Boolean))];
const withText = (r) => ({ ...r, keepText: (r.body.keep || []).join(', ') });
const emptyBody = () => ({ mapping: [], keep: [], auto: { schema: true, table: true, column: true, index: true, function: false, guess: true }, prefix: { schema: 'SCH', table: 'TB', column: 'COL', index: 'IX', function: 'FN', guess: 'COL' } });

/** 자동 이름 — 종류별 켜기·접두어. 변조 단계와 기준 편집이 함께 쓴다 */
export function AutoNames({ auto, prefix, onChange, disabled }) {
  const { t } = useTranslation();
  return (
    <div className="scol-auto">
      {RULE_KINDS.map(k => (
        <label key={k} className="scol-auto-row">
          <input type="checkbox" checked={!!auto[k]} disabled={disabled} onChange={e => onChange({ auto: { ...auto, [k]: e.target.checked }, prefix })} />
          <span className="scol-auto-kind">{t(`common:scol.kind.${k}`)}</span>
          <input className="scol-prefix mono" value={prefix[k] || ''} maxLength={12} disabled={disabled || !auto[k]} aria-label={t('common:scol.prefixOf', { kind: t(`common:scol.kind.${k}`) })}
            onChange={e => onChange({ auto, prefix: { ...prefix, [k]: e.target.value.toUpperCase() } })} />
          <span className="stn-note mono">{(prefix[k] || '')}001</span>
        </label>
      ))}
    </div>
  );
}
AutoNames.propTypes = { auto: PropTypes.object.isRequired, prefix: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };

export default function RedactRuleEditor({ selected, onSaved, onDeleted }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [rule, setRule] = useState(null);       // { id, name, body, history }
  const [saved, setSaved] = useState('');
  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [newKind, setNewKind] = useState('table');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!selected) { setRule(null); return undefined; }
    if (!selected.id) { const r = withText({ id: null, name: '', body: emptyBody(), history: [] }); setRule(r); setSaved(JSON.stringify(r)); return undefined; }
    api.getRule(selected.id).then((res) => { if (!alive) return; const r = withText({ ...res.rule, body: { ...emptyBody(), ...res.rule.body } }); setRule(r); setSaved(JSON.stringify(r)); })
      .catch(err => toast.error(formatApiError(err, t)));
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 고른 기준이 바뀔 때만
  }, [selected && selected.id, selected && selected.nonce]);

  if (!selected) return <div className="stn-empty-main">{t('common:scol.ruleIntro')}</div>;
  if (!rule) return <div className="stn-empty-main">{t('common:scol.loading')}</div>;
  const b = rule.body;
  const setBody = (patch) => setRule({ ...rule, body: { ...b, ...patch } });
  const dirty = JSON.stringify(rule) !== saved;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveRule({ id: rule.id, name: rule.name, body: { ...b, keep: parseKeep(rule.keepText) } });
      const next = withText({ ...r.rule, body: { ...emptyBody(), ...r.rule.body } });
      setRule(next); setSaved(JSON.stringify(next));
      toast.success(t('common:scol.ruleSaved'));
      onSaved(r.rule);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const remove = async () => {
    const ok = await confirm({ title: t('common:scol.ruleDeleteTitle'), message: t('common:scol.ruleDeleteBody', { name: rule.name }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteRule(rule.id); onDeleted(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const addRow = () => {
    const from = newFrom.trim().toUpperCase();
    if (!from || !newTo.trim()) return;
    if (b.mapping.some(m => m.from === from)) { toast.warning(t('common:scol.dupFrom', { name: from })); return; }
    setBody({ mapping: [{ from, to: newTo.trim(), kind: newKind }, ...b.mapping] });
    setNewFrom(''); setNewTo('');
  };
  const s = q.trim().toUpperCase();
  const rows = b.mapping.filter(m => !s || m.from.includes(s) || String(m.to).toUpperCase().includes(s));

  return (
    <div className="stn-step scol-rule sqq-fade" key={rule.id || 'new'}>
      <WorkflowHead
        titleSlot={<input className="stn-rename" value={rule.name} maxLength={80} placeholder={t('common:scol.ruleNamePh')} aria-label={t('common:scol.ruleName')}
          onChange={e => setRule({ ...rule, name: e.target.value })} />}
        meta={rule.id ? storedTime(rule.updatedAt, 'min') : t('common:scol.ruleNew')}
        dirty={dirty}
        onSave={save} saveDisabled={busy || !rule.name.trim() || !dirty}
        onDelete={rule.id ? remove : null} />
      <span className="stn-note">{t('common:scol.ruleHint')}</span>
      <div className="stn-box">
        <b>{t('common:scol.mappingTitle', { n: b.mapping.length })}</b>
        <div className="stn-row">
          <input className="mono" value={newFrom} onChange={e => setNewFrom(e.target.value)} placeholder={t('common:scol.fromPh')} aria-label={t('common:scol.from')} />
          <span>→</span>
          <input className="mono" value={newTo} onChange={e => setNewTo(e.target.value)} placeholder={t('common:scol.toPh')} aria-label={t('common:scol.to')}
            onKeyDown={e => { if (e.key === 'Enter') addRow(); }} />
          <select value={newKind} onChange={e => setNewKind(e.target.value)} aria-label={t('common:scol.kindLabel')}>
            {RULE_KINDS.map(k => <option key={k} value={k}>{t(`common:scol.kind.${k}`)}</option>)}
          </select>
          <button type="button" className="app-btn" onClick={addRow} disabled={!newFrom.trim() || !newTo.trim()}><FiPlus size={12} /> {t('common:scol.addMapping')}</button>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:scol.searchMapping')} aria-label={t('common:scol.searchMapping')} />
        </div>
        <div className="stn-table-wrap scol-map-wrap">
          <table className="stn-table">
            <thead><tr><th>{t('common:scol.from')}</th><th>{t('common:scol.to')}</th><th>{t('common:scol.kindLabel')}</th><th aria-label={t('common:ui.delete')} /></tr></thead>
            <tbody>
              {rows.slice(0, 500).map(m => (
                <tr key={m.from}>
                  <td className="mono">{m.from}</td>
                  <td><input className="mono scol-to" value={m.to} aria-label={t('common:scol.toOf', { name: m.from })}
                    onChange={e => setBody({ mapping: b.mapping.map(x => (x.from === m.from ? { ...x, to: e.target.value } : x)) })} /></td>
                  <td><select value={m.kind} aria-label={t('common:scol.kindLabel')} onChange={e => setBody({ mapping: b.mapping.map(x => (x.from === m.from ? { ...x, kind: e.target.value } : x)) })}>
                    {RULE_KINDS.map(k => <option key={k} value={k}>{t(`common:scol.kind.${k}`)}</option>)}
                  </select></td>
                  <td><button type="button" className="stn-icon-btn" title={t('common:ui.delete')} aria-label={t('common:ui.delete')} onClick={() => setBody({ mapping: b.mapping.filter(x => x.from !== m.from) })}><FiX size={12} /></button></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={4} className="stn-note">{t('common:scol.noMapping')}</td></tr>}
            </tbody>
          </table>
        </div>
        {rows.length > 500 && <span className="stn-note">{t('common:scol.mappingMore', { n: rows.length - 500 })}</span>}
      </div>
      <div className="stn-grid2">
        <div className="stn-box">
          <b>{t('common:scol.keepTitle')}</b>
          <textarea className="mono" rows={5} value={rule.keepText} placeholder={t('common:scol.keepPh')} aria-label={t('common:scol.keepTitle')}
            onChange={e => setRule({ ...rule, keepText: e.target.value })} />
          <span className="stn-note">{t('common:scol.keepHint')}</span>
        </div>
        <div className="stn-box">
          <b>{t('common:scol.autoTitle')}</b>
          <AutoNames auto={b.auto} prefix={b.prefix} onChange={({ auto, prefix }) => setBody({ auto, prefix })} />
          <span className="stn-note">{t('common:scol.autoHint')}</span>
        </div>
      </div>
      {rule.id && (rule.history || []).length > 0 && (
        <div className="stn-box">
          <b>{t('common:scol.ruleHistory')}</b>
          <ul className="sqq-hist">
            {[...rule.history].reverse().slice(0, 30).map((h, i) => (
              // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
              <li key={i}><span className="mono">{storedTime(h.at, 'min')}</span> {(h.changes || []).map(c => t(`common:scol.ruleChange.${c.type}`, { ...c })).join(' · ')}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

RedactRuleEditor.propTypes = {
  /** 고른 기준 { id } · 새 기준 { id: null, nonce } · 없음 null */
  selected: PropTypes.shape({ id: PropTypes.string, nonce: PropTypes.number }),
  onSaved: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};
